/**
 * FinancesController — page applicative /finances.html (EPIC-066 / 66.6,
 * refonte 99.69 — §31).
 *
 * Vue des mouvements financiers d'une période : encaissés / à encaisser
 * (entrées) et payés / à payer (sorties), avec un Net honnête (jamais un
 * « solde bancaire »). Un seul appel `GET /finances` au connect() et
 * à chaque changement de période. Aucun filtre secondaire (ni état, ni
 * client, ni catégorie — décisions EPIC-066 #7 et 99.69) : la liste
 * montre tous les mouvements de la période, pastilles par ligne.
 *
 * Sélecteur de période à 1 niveau : 6 presets directs (§31.C), bouton
 * actif en style plein (`nm-btn--solid`). Le preset effectif est persisté
 * en querystring (`?period=`, + `from`/`to` pour le personnalisé) et le
 * serveur reste autoritaire sur le preset par défaut (`encours_trimestre`
 * quelle que soit la cadence, §31.D) : au 1er appel sans `period`, on
 * adopte `data.period.preset`. Un preset valide hors UI (`ecoule_mois`…)
 * arrivé par URL est chargé tel quel : aucun bouton actif, mais le
 * libellé de période en toutes lettres dit toujours les dates.
 *
 * Pattern `<template>` + clone + remplissage manuel des slots — aucune
 * construction HTML en chaîne dans le controller (CLAUDE.md frontend).
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";

const AMOUNT_FORMATTER = new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

const PRESETS = [
    "ecoule_mois",
    "ecoule_trimestre",
    "ecoule_annee",
    "encours_mois",
    "encours_trimestre",
    "encours_annee",
    "avenir_30",
    "avenir_60",
    "avenir_90",
    "perso",
];

// Libellé du preset pour la ligne « période résolue » en toutes lettres
// (§31.C) — couvre aussi les presets valides hors UI arrivés par URL.
const PRESET_LABELS = {
    ecoule_mois: "Mois dernier",
    ecoule_trimestre: "Trimestre dernier",
    ecoule_annee: "Année dernière",
    encours_mois: "Mois en cours",
    encours_trimestre: "Trimestre en cours",
    encours_annee: "Année en cours",
    avenir_30: "30 prochains jours",
    avenir_60: "60 prochains jours",
    avenir_90: "90 prochains jours",
};

const DAY_MONTH_FORMATTER = new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "long" });
const DAY_MONTH_YEAR_FORMATTER = new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "long", year: "numeric" });

// Libellé + classe de pastille par nature de flux (§31.C).
const STATE_PILL = {
    encaisse: { label: "Encaissé", class: "nm-pill nm-pill--olive" },
    a_encaisser: { label: "À encaisser", class: "nm-pill nm-pill--blue" },
    paye: { label: "Payé", class: "nm-pill" },
    a_payer: { label: "À payer", class: "nm-pill nm-pill--ochre" },
};

export default class FinancesController extends BaseController {
    static targets = [
        "overdueBanner",
        "overdueAmount",
        "overdueCount",
        "overdueOldest",
        "presetBtn",
        "periodLabel",
        "persoRow",
        "persoFrom",
        "persoTo",
        "persoError",
        "realizedIn",
        "realizedOut",
        "realizedNet",
        "futureIn",
        "futureOut",
        "futureNet",
        "netAmount",
        "netMention",
        "results",
        "list",
        "empty",
        "error",
        "rowTemplate",
        "urssafBlock",
        "urssafPeriod",
        "urssafCa",
        "urssafCotisations",
        "urssafSource",
        "urssafProgress",
        "urssafDays",
        "urssafDueDate",
        "urssafCta",
    ];

    connect() {
        this.allLines = [];

        this.popstateHandler = () => {
            this.restoreFromUrl();
            this.reflectFilters();
            this.load();
        };
        window.addEventListener("popstate", this.popstateHandler);

        this.restoreFromUrl();
        this.reflectFilters();
        this.load();
    }

    disconnect() {
        if (this.popstateHandler) {
            window.removeEventListener("popstate", this.popstateHandler);
        }
    }

    // -------------------------------------------------------------------------
    // État ↔ URL
    // -------------------------------------------------------------------------

    restoreFromUrl() {
        const params = new URLSearchParams(window.location.search);
        const periodRaw = params.get("period") ?? "";
        // `null` → le serveur choisit le preset par défaut (`encours_trimestre`).
        this.preset = PRESETS.includes(periodRaw) ? periodRaw : null;
        this.from = params.get("from") ?? "";
        this.to = params.get("to") ?? "";
        // `?state=` (ancien filtre par état, retiré en 99.69) est ignoré et
        // ne sera plus jamais réécrit par updateUrl().
    }

    updateUrl() {
        const params = new URLSearchParams();
        if (this.preset) params.set("period", this.preset);
        if (this.preset === "perso") {
            if (this.from) params.set("from", this.from);
            if (this.to) params.set("to", this.to);
        }

        const query = params.toString();
        const url = query ? `${window.location.pathname}?${query}` : window.location.pathname;
        window.history.pushState(null, "", url);
    }

    reflectFilters() {
        // Bouton sélectionné en style plein (§31.C) — un preset valide hors
        // UI (`ecoule_mois`…) ne correspond à aucun bouton : tous restent ghost.
        for (const button of this.presetBtnTargets) {
            const active = button.dataset.preset === this.preset;
            button.classList.toggle("nm-btn--solid", active);
            button.classList.toggle("nm-btn--ghost", !active);
        }
        if (this.hasPersoRowTarget) {
            this.persoRowTarget.hidden = this.preset !== "perso";
        }
        if (this.preset === "perso") {
            if (this.hasPersoFromTarget && this.from) this.persoFromTarget.value = this.from;
            if (this.hasPersoToTarget && this.to) this.persoToTarget.value = this.to;
        }
    }

    // -------------------------------------------------------------------------
    // Actions (DOM → état)
    // -------------------------------------------------------------------------

    onPresetChange(event) {
        const preset = event.currentTarget.dataset.preset;
        if (!PRESETS.includes(preset)) return;

        if (preset === "perso") {
            this.preset = "perso";
            this.reflectFilters();
            if (this.hasPersoErrorTarget) this.persoErrorTarget.hidden = true;
            // Pas de fetch tant que les deux dates ne sont pas validées.

            return;
        }

        this.preset = preset;
        this.from = "";
        this.to = "";
        this.reflectFilters();
        this.updateUrl();
        this.load();
    }

    applyPerso() {
        const from = this.hasPersoFromTarget ? this.persoFromTarget.value : "";
        const to = this.hasPersoToTarget ? this.persoToTarget.value : "";
        if (!from || !to) {
            this.showPersoError("Indiquez une date de début et une date de fin.");

            return;
        }
        if (from > to) {
            this.showPersoError("La date de début doit précéder la date de fin.");

            return;
        }
        if (this.hasPersoErrorTarget) this.persoErrorTarget.hidden = true;
        this.preset = "perso";
        this.from = from;
        this.to = to;
        this.updateUrl();
        this.load();
    }

    showPersoError(message) {
        if (!this.hasPersoErrorTarget) return;
        this.persoErrorTarget.textContent = message;
        this.persoErrorTarget.hidden = false;
    }

    // -------------------------------------------------------------------------
    // Appel API + rendu
    // -------------------------------------------------------------------------

    async load() {
        this.showLoadingCards();
        this.showLoadingList();
        try {
            const params = {};
            if (this.preset) params.period = this.preset;
            if (this.preset === "perso") {
                params.from = this.from;
                params.to = this.to;
            }
            const res = await api.getFinances(params);
            const data = res?.data ?? res ?? {};

            // Le serveur est autoritaire sur le preset résolu (défaut
            // `encours_trimestre`, §31.D).
            const resolved = data.period?.preset;
            if (PRESETS.includes(resolved)) {
                this.preset = resolved;
                this.reflectFilters();
            }
            this.renderPeriodLabel(data.period);

            this.allLines = Array.isArray(data.lines) ? data.lines : [];
            this.renderOverdue(data.overdue);
            this.renderCards(data.cards);
            this.renderUrssaf(data.accrual);
            this.renderList();
        } catch {
            // 401 déjà géré globalement par api.js (redirect /connexion.html).
            this.showError();
        }
    }

    renderOverdue(overdue) {
        if (!this.hasOverdueBannerTarget) return;
        const count = overdue?.invoiceCount ?? 0;
        if (!count || count <= 0) {
            this.overdueBannerTarget.hidden = true;

            return;
        }
        if (this.hasOverdueAmountTarget) {
            this.overdueAmountTarget.textContent = this.money(overdue.amount ?? 0);
        }
        if (this.hasOverdueCountTarget) {
            this.overdueCountTarget.textContent = `${count} facture${count > 1 ? "s" : ""}`;
        }
        if (this.hasOverdueOldestTarget) {
            const days = overdue.oldestDays ?? 0;
            this.overdueOldestTarget.textContent = `${days} jour${days > 1 ? "s" : ""}`;
        }
        this.overdueBannerTarget.hidden = false;
    }

    /**
     * Libellé de la période résolue en toutes lettres sous le sélecteur
     * (§31.C) — ex. « Trimestre en cours — du 1 avril au 30 juin 2026 »,
     * et pour le personnalisé « Du 12 mai au 30 juin 2026 ».
     */
    renderPeriodLabel(period) {
        if (!this.hasPeriodLabelTarget) return;
        const from = this.parseIsoDay(period?.from);
        const to = this.parseIsoDay(period?.to);
        if (!from || !to) {
            this.periodLabelTarget.textContent = "—";

            return;
        }
        // L'année du début est tue quand elle est la même que celle de fin.
        const sameYear = from.getFullYear() === to.getFullYear();
        const fromText = (sameYear ? DAY_MONTH_FORMATTER : DAY_MONTH_YEAR_FORMATTER).format(from);
        const toText = DAY_MONTH_YEAR_FORMATTER.format(to);
        const prefix = PRESET_LABELS[period?.preset];
        this.periodLabelTarget.textContent = prefix
            ? `${prefix} — du ${fromText} au ${toText}`
            : `Du ${fromText} au ${toText}`;
    }

    parseIsoDay(value) {
        if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
        const [year, month, day] = value.split("-").map(Number);

        return new Date(year, month - 1, day);
    }

    /**
     * Les 3 cartes sont toujours affichées quel que soit le preset
     * (§31.C, révision C-08) — une partie sans mouvement vaut 0 €.
     */
    renderCards(cards) {
        const realized = cards?.realized ?? {};
        const future = cards?.future ?? {};
        const net = cards?.net ?? {};

        this.realizedInTarget.textContent = `+ ${this.money(realized.in ?? 0)}`;
        this.realizedOutTarget.textContent = `− ${this.money(realized.out ?? 0)}`;
        this.setSigned(this.realizedNetTarget, realized.net ?? 0);

        this.futureInTarget.textContent = `+ ${this.money(future.in ?? 0)}`;
        this.futureOutTarget.textContent = `− ${this.money(future.out ?? 0)}`;
        this.setSigned(this.futureNetTarget, future.net ?? 0);

        if (net.isAsymmetric === true) {
            // Net grisé : la période ne porte pas de réalisé, le chiffre
            // est purement prévisionnel (§31.E).
            this.netAmountTarget.textContent = this.signed(net.amount ?? 0);
            this.netAmountTarget.className = "nm-stat__v fs-28 text-muted";
            this.netAmountTarget.style.cssText = "font-family: var(--serif); font-style: italic; font-weight: 400; margin-top: 12px;";
            if (this.hasNetMentionTarget) this.netMentionTarget.hidden = false;
        } else {
            this.setSigned(this.netAmountTarget, net.amount ?? 0, true);
            if (this.hasNetMentionTarget) this.netMentionTarget.hidden = true;
        }
    }

    renderUrssaf(accrual) {
        if (!this.hasUrssafBlockTarget) return;
        if (!accrual) {
            this.urssafBlockTarget.hidden = true;

            return;
        }
        if (this.hasUrssafPeriodTarget) this.urssafPeriodTarget.textContent = accrual.urssafPeriod ?? "—";
        if (this.hasUrssafCaTarget) this.urssafCaTarget.textContent = this.money(accrual.caEncaisse ?? 0);
        if (this.hasUrssafCotisationsTarget) {
            this.urssafCotisationsTarget.textContent = this.money(accrual.estimatedCotisations ?? 0);
        }
        if (this.hasUrssafSourceTarget) {
            this.urssafSourceTarget.textContent = accrual.estimationSource === "declaration_draft"
                ? "Estimation basée sur votre déclaration en cours de préparation."
                : "Estimation basée sur les taux de cotisation en vigueur.";
        }
        if (this.hasUrssafProgressTarget) {
            const total = accrual.daysTotal ?? 0;
            const elapsed = accrual.daysElapsed ?? 0;
            const pct = total > 0 ? Math.min(100, Math.round((elapsed / total) * 100)) : 0;
            this.urssafProgressTarget.value = pct;
        }
        if (this.hasUrssafDaysTarget) {
            const total = accrual.daysTotal ?? 0;
            const elapsed = accrual.daysElapsed ?? 0;
            this.urssafDaysTarget.textContent = `${elapsed} jour${elapsed > 1 ? "s" : ""} sur ${total}`;
        }
        if (this.hasUrssafDueDateTarget) {
            this.urssafDueDateTarget.textContent = this.formatDate(accrual.declarationDueDate);
        }
        if (this.hasUrssafCtaTarget && accrual.ctaUrl) {
            this.urssafCtaTarget.setAttribute("href", accrual.ctaUrl);
        }
        this.urssafBlockTarget.hidden = false;
    }

    renderList() {
        if (!this.hasListTarget) return;

        const today = this.todayIso();

        // Lignes en retard (à encaisser / à payer échues) remontées en tête.
        const overdue = [];
        const rest = [];
        for (const line of this.allLines) {
            if (this.isLineOverdue(line, today)) {
                overdue.push(line);
            } else {
                rest.push(line);
            }
        }
        const ordered = [...overdue, ...rest];

        if (ordered.length === 0) {
            this.showEmpty();

            return;
        }

        this.listTarget.replaceChildren();
        for (const line of ordered) {
            const fragment = this.rowTemplateTarget.content.cloneNode(true);
            this.fillRow(fragment, line, today);
            this.listTarget.appendChild(fragment);
        }
        this.showList();
    }

    isLineOverdue(line, today) {
        return (line.category === "a_encaisser" || line.category === "a_payer")
            && typeof line.date === "string"
            && line.date < today;
    }

    fillRow(fragment, line, today) {
        const dateEl = fragment.querySelector('[data-slot="date"]');
        const amountEl = fragment.querySelector('[data-slot="amount"]');
        const labelEl = fragment.querySelector('[data-slot="label"]');
        const pillEl = fragment.querySelector('[data-slot="statePill"]');
        const linkEl = fragment.querySelector('[data-slot="link"]');
        const nudgeEl = fragment.querySelector('[data-slot="nudge"]');

        const overdue = this.isLineOverdue(line, today);

        if (dateEl) dateEl.textContent = this.formatShortDate(line.date);

        if (amountEl) {
            const amount = line.amount ?? 0;
            amountEl.textContent = this.signed(amount);
            amountEl.classList.add(amount < 0 ? "text-error" : "text-success");
        }

        if (labelEl) labelEl.textContent = line.label ?? "";

        if (pillEl) {
            const meta = STATE_PILL[line.category] ?? { label: line.category ?? "", class: "nm-pill" };
            pillEl.className = overdue ? "nm-pill nm-pill--red" : meta.class;
            pillEl.replaceChildren();
            const dot = document.createElement("span");
            dot.className = "nm-pill__dot";
            pillEl.appendChild(dot);
            pillEl.append(overdue ? "En retard" : meta.label);
        }

        if (linkEl) {
            if (line.linkUrl) {
                linkEl.setAttribute("href", line.linkUrl);
                linkEl.hidden = false;
            } else {
                linkEl.remove();
            }
        }

        if (nudgeEl) {
            // Nudge §15.D : sur une charge à payer échue, renvoyer vers les
            // échéances pour la marquer réglée (le form inline vit là — 66.2).
            if (overdue && line.category === "a_payer") {
                nudgeEl.hidden = false;
            } else {
                nudgeEl.remove();
            }
        }
    }

    // -------------------------------------------------------------------------
    // Bascule de visibilité
    // -------------------------------------------------------------------------

    showLoadingCards() {
        for (const name of ["periodLabel", "realizedIn", "realizedOut", "realizedNet", "futureIn", "futureOut", "futureNet", "netAmount"]) {
            const target = this[`${name}Target`];
            if (target) target.replaceChildren(this.loadingPlaceholder());
        }
    }

    loadingPlaceholder() {
        const span = document.createElement("span");
        span.className = "nm-loading";
        for (let i = 0; i < 3; i += 1) {
            const dash = document.createElement("i");
            dash.textContent = "—";
            span.appendChild(dash);
        }

        return span;
    }

    showLoadingList() {
        if (this.hasErrorTarget) this.errorTarget.hidden = true;
        if (this.hasEmptyTarget) this.emptyTarget.hidden = true;
        if (this.hasResultsTarget) this.resultsTarget.hidden = false;
        if (!this.hasListTarget) return;
        this.listTarget.replaceChildren();
        const tr = document.createElement("tr");
        tr.className = "nm-table-loading";
        const td = document.createElement("td");
        td.colSpan = 5;
        td.append("Chargement");
        const dots = document.createElement("span");
        dots.className = "nm-table-loading__dots";
        dots.append(document.createElement("i"), document.createElement("i"), document.createElement("i"));
        td.appendChild(dots);
        tr.appendChild(td);
        this.listTarget.appendChild(tr);
    }

    showList() {
        if (this.hasResultsTarget) this.resultsTarget.hidden = false;
        if (this.hasEmptyTarget) this.emptyTarget.hidden = true;
        if (this.hasErrorTarget) this.errorTarget.hidden = true;
    }

    showEmpty() {
        if (this.hasResultsTarget) this.resultsTarget.hidden = true;
        if (this.hasEmptyTarget) this.emptyTarget.hidden = false;
        if (this.hasErrorTarget) this.errorTarget.hidden = true;
    }

    showError() {
        if (this.hasPeriodLabelTarget) this.periodLabelTarget.textContent = "—";
        if (this.hasResultsTarget) this.resultsTarget.hidden = true;
        if (this.hasEmptyTarget) this.emptyTarget.hidden = true;
        if (this.hasErrorTarget) this.errorTarget.hidden = false;
    }

    // -------------------------------------------------------------------------
    // Helpers
    // -------------------------------------------------------------------------

    money(value) {
        return `${AMOUNT_FORMATTER.format(Math.abs(Number(value) || 0))} €`;
    }

    signed(value) {
        const n = Number(value) || 0;
        const sign = n < 0 ? "−" : "+";

        return `${sign} ${this.money(n)}`;
    }

    setSigned(target, value, applyStyle = false) {
        if (!target) return;
        const n = Number(value) || 0;
        target.textContent = this.signed(n);
        if (applyStyle) {
            target.className = `nm-stat__v fs-28 ${n < 0 ? "text-error" : "text-success"}`;
            target.style.cssText = "font-family: var(--serif); font-style: italic; font-weight: 400; margin-top: 12px;";
        } else {
            target.className = `nm-totals__v ${n < 0 ? "text-error" : "text-success"}`;
        }
    }

    todayIso() {
        const d = new Date();
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");

        return `${y}-${m}-${day}`;
    }
}
